import React from 'react';
import PropTypes from 'prop-types';
import $ from 'jquery';
import CropImageComponent from './CropImageComponent';
import AddTextComponent from './AddTextComponent';
import AssetAnnotationsComponent from './AssetAnnotationsComponent';
import randomstring from 'randomstring';
import {Rnd} from 'react-rnd';
import Authenticator from './../protocol/Authenticator';
import DCUtil from '../util/DrawingCanvasUtil';
import {calculateOverflow} from '../util/DOMUtil';
import loginRequired from '../auth/loginRequired';
import Store from '../store/Store';
import {PERMISSIONS, DRAWING_CANVAS} from '../store/Constants';

import './assets/DrawingCanvasStyle.css';
import AnnotationUtil from '../queries/annotations/AnnotationUtil';

/**
 * Multipage PDFs may cause the canvas size to be large. The max size
 * in many browsers for canvas is 32767 (signed 2^16 - 1);
 */
const MAX_HEIGHT = 32767;

export default class DrawingCanvas extends React.Component {
  static propTypes = {
    imageSrc: PropTypes.string,
    isUnavailable: PropTypes.bool,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    currentPage: PropTypes.number,
    numPages: PropTypes.number,
    isContinuousPage: PropTypes.bool,
    shapeDrawingStart: PropTypes.func,
    shapeDrawingStop: PropTypes.func,
    updatePage: PropTypes.func,
    onTextInputHide: PropTypes.func,
    onRender: PropTypes.func,
    onClickThroughShape: PropTypes.func,
    shapes: PropTypes.array.isRequired,
    addingShape: PropTypes.object,
    onShapeCreated: PropTypes.func,
    onShapeDeleted: PropTypes.func,
    onShapeDrag: PropTypes.func,
    onShapeDragEnd: PropTypes.func,
    isTemporal: PropTypes.bool,
    onCropResizeStop: PropTypes.func,
  };

  static defaultProps = {
    currentPage: 1,
    numPages: 1,
    isContinuousPage: false,
    onTextInputHide: $.noop,
    onClickThroughShape: $.noop,
    isTemporal: false,
  };

  static DRAWING_MODE_TEXT = 'text';
  static DRAWING_MODE_RECT = 'rect';
  static DRAWING_MODE_ELLIPSE = 'ellipse';
  static DRAWING_MODE_LINE = 'line';

  constructor(props) {
    super(props);

    this.state = {
      scale: 1,
      offsetX: 0,
      offsetY: 0,
      containerWidth: 0,
      containerHeight: 0,
      isDrawing: false,
      drawingMode: null,
      cropMode: false,
      textModeState: null,
      textProps: null,
      canScroll: props.isContinuousPage,
      infScrollStartPage: 1,
      scrollTopOffset: 0,
      rndBoxState: {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        drawRndBox: false,
        allowResize: true,
        drawLineEdges: false,
      },
      textElements: DCUtil.makeEmptySVG(0, 0),
    };

    this.img = null;
    this.lostInputFocusInterval = null;
    this.fontPickerDisappearTimeout = null;
    this.lastId = 0;
    this.needUpdateOffsetY = true;
    this._scrollTop = 0;
    this._preventInfScroll = false;
    this._recalculateScrollTop = false;
    this.resizing = false;

    this.currentScale = 1;

    this.onDOMDrawn = this.onDOMDrawn.bind(this);
    this.userSelectedInputTextArea = this.userSelectedInputTextArea.bind(this);
    this.textChangedByUser = this.textChangedByUser.bind(this);
    this.textColorChanged = this.textColorChanged.bind(this);
    this.textFontChanged = this.textFontChanged.bind(this);
    this.textDeleted = this.textDeleted.bind(this);
    this.handleEditEvent = this.handleEditEvent.bind(this);
    this.handleShapeDrag = this.handleShapeDrag.bind(this);
    this.handleShapeResize = this.handleShapeResize.bind(this);
    this.alignRndBox = this.alignRndBox.bind(this);
  }

  componentDidMount() {
    this.drawCanvas();

    // TODO: remove this hack and eventually remove this entire component.
    Store.put(DRAWING_CANVAS, this);
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.isContinuousPage && nextProps.isContinuousPage) {
      this._scrollTop = 0;
      this.setPreventInfScrollTimer();
      this.setState({canScroll: true, infScrollStartPage: this.getInfScrollStartPage(nextProps)});
    } else if (this.props.isContinuousPage && !nextProps.isContinuousPage) {
      this.setState({canScroll: false});
    } else if (nextProps.isContinuousPage && this.state.canScroll && this._recalculateScrollTop) {
      const infScrollStartPage = this.getInfScrollStartPage(nextProps);
      this.setPreventInfScrollTimer();
      this.setState({infScrollStartPage});
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.drawCanvas();

    if (this.state.cropMode && prevProps.scale !== prevState.scale) {
      this.refs.crop.setState({
        scale: prevState.scale,
        offsetX: prevState.offsetX,
        offsetY: prevState.offsetY,
      });
    }
    if (this.state.rndBoxState.drawRndBox && this.refs.box) {
      this.alignRndBox();
    }

    if (this.props.isContinuousPage && this.state.canScroll && this._recalculateScrollTop) {
      this._recalculateScrollTop = false;
      setTimeout(() => {
        const {currentPage, numPages} = this.props;
        const {infScrollStartPage} = this.state;
        const {scrollContainer, container} = this.refs;
        const pageHeight = this.getPageHeight();
        if (!scrollContainer) {
          return;
        }

        this._ignoreNextScrollEvent = true;

        let scrollTop;
        if (currentPage > numPages - 10) {
          scrollTop =
            scrollContainer.scrollHeight -
            scrollContainer.clientHeight -
            (numPages - currentPage) * pageHeight;
          const containerTop = parseInt(container.style.top.replace('px', ''), 10);
          if (containerTop !== scrollTop) {
            this.setState({scrollTopOffset: scrollTop - containerTop});
          }
        } else {
          scrollTop = (currentPage - 1) * pageHeight - (infScrollStartPage - 1) * pageHeight;
          this.setState({scrollTopOffset: 0});
        }

        scrollContainer.scrollTop = this._scrollTop = scrollTop;
      }, 100);
    }
  }

  componentWillUnmount() {
    Store.put(DRAWING_CANVAS, null);
  }

  drawCanvas() {
    if (!this.props.imageSrc) return;
    if (this.img === null || this.img.src !== this.props.imageSrc) {
      this.img = new Image();
      this.img.src = this.props.imageSrc;
      const scope = this;
      this.img.onload = function() {
        scope.drawCanvas();
      };
    } else {
      const canvas = this.refs.canvas;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        this.img,
        this.state.offsetX,
        this.state.offsetY,
        this.props.width * this.state.scale,
        this.props.height * this.state.scale
      );
    }
  }

  onDOMDrawn() {
    const scope = this;
    window.requestAnimationFrame(function() {
      if (!scope.refs.container || scope.refs.container.offsetHeight === 0) return;
      // const offsetHeight = window.innerHeight - 70;
      const offsetHeight = scope.refs.container.offsetHeight;
      const ratio = Math.min(
        scope.refs.container.offsetWidth / scope.props.width,
        offsetHeight / scope.props.height
      );
      scope.currentScale = ratio;
      if (
        scope.state.scale !== ratio ||
        scope.state.containerWidth !== scope.refs.container.offsetWidth ||
        (!scope.props.isContinuousPage && scope.state.containerHeight !== offsetHeight)
      ) {
        if (ratio !== 1) {
          scope.currentScale = ratio;
          scope.setState({
            scale: ratio,
            offsetX: (scope.refs.container.offsetWidth - scope.props.width * ratio) / 2,
            offsetY: scope.needUpdateOffsetY
              ? (offsetHeight - scope.props.height * ratio) / 2
              : scope.state.offsetY,
            containerWidth: scope.refs.container.offsetWidth,
            containerHeight: offsetHeight,
          });
          if (scope.props.onScaleChange) scope.props.onScaleChange(ratio);
        } else {
          scope.currentScale = 1;
          scope.setState({
            scale: 1,
            offsetX: 0,
            offsetY: 0,
            containerWidth: scope.refs.container.offsetWidth,
            containerHeight: offsetHeight,
          });
        }
      }
    });
    if (this.props.onRender) this.props.onRender();
  }

  toggleTextInput() {
    if (this.state.isDrawing && this.state.drawingMode === this.DRAWING_MODE_TEXT) {
      this.maybeEnableScroll();
      this.setState({
        isDrawing: false,
        drawingMode: null,
        textModeState: null,
      });
      this.cleanTextArea();
      this.clearFontPickerDisappearInterval();
      window.render();
    } else {
      this.maybeDisableScroll();
      const halfTextAreaWidth = 75 * this.state.scale;
      this.userSelectedInputTextArea({
        nativeEvent: {
          offsetX: (this.props.width * this.state.scale) / 2 - halfTextAreaWidth,
          offsetY: (this.props.height * this.state.scale) / 2,
        },
      });
    }
  }

  @loginRequired
  toggleShapeInput(type) {
    switch (type) {
      case 'rect':
        this.placeRect();
        break;
      case 'ellipse':
        this.placeEllipse();
        break;
      case 'line':
        this.placeLine();
        break;
      default:
        console.log('Unsupported shape:', type);
    }
  }

  getNewShapeSize = () => {
    const {width, height} = this.getDisplayProps();
    const smallSide = Math.min(width, height);
    const min = 50 / this.state.scale;
    const max = 100 / this.state.scale;

    // Start with 1/4 the size of the asset.
    let size = smallSide / 4 / this.state.scale;

    // Apply thresholds.
    size = Math.max(size, min);
    size = Math.min(size, max);

    return size;
  };

  placeRect() {
    const size = this.getNewShapeSize();
    this.refs.annotations.placeRect(
      this.props.width / 2 - size / 2,
      this.props.height / 2 - size / 2,
      size,
      size,
      {
        fillOpacity: 0.0,
        stroke: 'red',
        strokeWidth: '3',
      }
    );
    this.resetActiveShape();
  }

  placeEllipse() {
    const size = this.getNewShapeSize();
    this.refs.annotations.placeEllipse(
      this.props.width / 2,
      this.props.height / 2,
      size / 2,
      size / 2,
      {
        fillOpacity: 0.0,
        stroke: 'red',
        strokeWidth: '3',
      }
    );
    this.resetActiveShape();
  }

  placeLine() {
    const size = this.getNewShapeSize() / 2;
    this.refs.annotations.placeLine(
      this.props.width / 2 - size,
      this.props.height / 2 - size,
      this.props.width / 2 + size,
      this.props.height / 2 + size,
      {fill: 'none', stroke: 'red', strokeWidth: '5'}
    );
    this.resetActiveShape();
  }

  toggleCrop() {
    if (this.state.cropMode) {
      this.maybeEnableScroll();
    } else {
      this.maybeDisableScroll();
    }

    this.setState({cropMode: !this.state.cropMode});
  }

  getCropSelection() {
    if (this.state.cropMode) {
      let selection = this.refs.crop.getSelection();
      selection.x = Math.floor(selection.x / this.state.scale);
      selection.y = Math.floor(selection.y / this.state.scale);
      selection.width = Math.floor(selection.width / this.state.scale);
      selection.height = Math.floor(selection.height / this.state.scale);
      return selection;
    } else {
      return null;
    }
  }

  getAnnotations() {
    return this.refs.annotations.getSVG();
  }

  getDisplayProps() {
    if (this.state.scale === Infinity) {
      return null;
    }

    return {
      x: this.state.offsetX,
      y: this.state.offsetY,
      width: Math.floor(this.props.width * this.state.scale),
      height: Math.floor(this.props.height * this.state.scale),
    };
  }

  drawAnnotations() {
    if (this.props.isUnavailable) {
      return null;
    }

    const width = Math.floor(this.props.width * this.state.scale);
    const height = Math.floor(this.props.height * this.state.scale);
    const annotationsStyle = {
      position: 'absolute',
      top: this.state.offsetY,
      left: this.state.offsetX,
      width: width,
      height: height,
      zIndex: this.state.textModeState && this.state.textModeState.userClicked ? 1 : 0,
    };
    return (
      <div style={annotationsStyle}>
        <AssetAnnotationsComponent
          ref="annotations"
          shapes={this.props.shapes}
          addingShape={this.props.addingShape}
          attachments={this.props.attachments}
          onNewElement={this.handleEditEvent}
          onShapeCreated={this.props.onShapeCreated}
          onShapeDeleted={shape => {
            this.props.onShapeDeleted(shape);
            this.resetActiveShape();
          }}
          onShapeDrag={this.props.onShapeDrag}
          onShapeDragEnd={this.props.onShapeDragEnd}
          isTemporal={this.props.isTemporal}
          textElements={this.state.textElements}
          parent={this}
        />
      </div>
    );
  }

  drawInput() {
    if (this.props.isUnavailable) {
      return null;
    }

    if (this.state.isDrawing) {
      switch (this.state.drawingMode) {
        case this.DRAWING_MODE_TEXT:
          return this.drawAddTextComponent();
        default:
          return null;
      }
    }
  }

  handleAddTextComponentMove = (x, y) => {
    let {textModeState} = this.state;
    textModeState.x = x / this.state.scale;
    textModeState.y = (y + 20) / this.state.scale;
    this.setState({textModeState});
  };

  drawAddTextComponent() {
    const width = Math.floor(this.props.width * this.state.scale);
    const height = Math.floor(this.props.height * this.state.scale);
    const textInputWrap = {
      ...styles.textInputWrap,
      top: this.state.offsetY,
      left: this.state.offsetX,
      width: width,
      height: height,
    };
    return (
      <div style={textInputWrap}>
        {this.state.textModeState.userClicked ? (
          <AddTextComponent
            ref="addTextComp"
            id={this.state.textProps.id}
            textTop={this.state.textModeState.y * this.state.scale - 22}
            textLeft={this.state.textModeState.x * this.state.scale}
            textValue={this.state.textProps.textValue}
            color={this.state.textProps.textColor}
            font={this.state.textProps.font}
            fontSize={this.state.textProps.textSize}
            submitValue={this.textChangedByUser}
            colorChanged={this.textColorChanged}
            fontChanged={this.textFontChanged}
            deleteValue={this.textDeleted}
            fontStyle={{
              bold: this.state.textProps.bold,
              italic: this.state.textProps.italic,
              underline: this.state.textProps.underline,
            }}
            onMove={this.handleAddTextComponentMove}
          />
        ) : // : <div
        //     style={ styles.textInputClickArea }
        //     onClick={ this.userSelectedInputTextArea }/>
        null}
      </div>
    );
  }

  clearFontPickerDisappearInterval() {
    if (this.fontPickerDisappearTimeout) {
      clearInterval(this.fontPickerDisappearTimeout);
      this.fontPickerDisappearTimeout = null;
    }
  }

  onShapeClick = event => {
    const isText = event.currentTarget.nodeName === 'text';
    const isShape = event.currentTarget.getAttribute('shape') === 'true';
    if (this.state.isDrawing || this.refs.annotations.isDragging()) {
      this.maybeEnableScroll();
      return;
    }

    if (!isText) {
      const {canWriteComments, isAdmin} = Store.get(PERMISSIONS);
      const shape = this.refs.annotations.findShapeById(event.target.id);
      const isCreator = AnnotationUtil.isIamCreatorOfThisThread(shape.comment);
      const canEdit = isAdmin || (canWriteComments && isCreator);
      if (!canEdit) return;
    }

    this.maybeDisableScroll();
    if (isText) {
      let text = event.currentTarget;
      const fontSize = parseInt(text.getAttribute('font-size'), 10);
      const fontHeight = fontSize - (fontSize - fontSize / 1.180952381);
      this.refs.annotations.removeElem(text);
      this.userSelectedInputTextArea(
        {
          nativeEvent: {
            offsetX: (parseInt(text.getAttribute('x'), 10) - 6) * this.state.scale,
            offsetY: (parseInt(text.getAttribute('y'), 10) - 5 - fontHeight + 1) * this.state.scale,
          },
        },
        {
          textValue: DCUtil.decodeFullText(text.getAttribute('full-text')),
          textColor: text.getAttribute('fill'),
          textSize: parseInt(text.getAttribute('font-size'), 10),
          font: text.getAttribute('font-family'),
          fontWeight: text.getAttribute('font-weight'),
          fontStyle: text.getAttribute('font-style'),
          textDecoration: text.getAttribute('text-decoration'),
          bold: text.getAttribute('font-weight') === '700',
          italic: text.getAttribute('font-style') === 'italic',
          underline: text.getAttribute('text-decoration').includes('underline'),
          width: text.getAttribute('width'),
          height: text.getAttribute('height'),
          id: text.getAttribute('ezdcid'),
        }
      );
    } else if (isShape) {
      if (!this.doRegisterClickOnShape(event)) {
        this.onClickThroughShape(event);
        return;
      }
      this.props.shapeDrawingStart();
      this.refs.annotations.handleShapeClick(event.currentTarget);
      switch (event.currentTarget.nodeName) {
        case 'rect':
          {
            const rectEl = event.currentTarget;
            let x = parseInt(rectEl.getAttribute('x'), 10) - 10;
            let y = parseInt(rectEl.getAttribute('y'), 10) - 10;
            let width = parseInt(rectEl.getAttribute('width'), 10) + 20;
            let height = parseInt(rectEl.getAttribute('height'), 10) + 20;
            this.setState({
              isDrawing: true,
              rndBoxState: {
                x: x * this.state.scale,
                y: y * this.state.scale,
                width: width * this.state.scale,
                height: height * this.state.scale,
                drawRndBox: true,
                allowResize: true,
                drawLineEdges: false,
              },
            });
            this.bootstrapShape();
          }
          break;
        case 'ellipse':
          {
            const ellipseEl = event.currentTarget;
            let x = parseInt(ellipseEl.getAttribute('cx'), 10);
            let y = parseInt(ellipseEl.getAttribute('cy'), 10);
            let width = parseInt(ellipseEl.getAttribute('rx'), 10);
            let height = parseInt(ellipseEl.getAttribute('ry'), 10);
            y -= height + 10;
            x -= width + 10;
            width += width + 20;
            height += height + 20;
            this.setState({
              isDrawing: true,
              rndBoxState: {
                x: x * this.state.scale,
                y: y * this.state.scale,
                width: width * this.state.scale,
                height: height * this.state.scale,
                drawRndBox: true,
                allowResize: true,
                drawLineEdges: false,
              },
            });
            this.bootstrapShape();
          }
          break;
        case 'line':
          {
            const lineEl = event.currentTarget;
            const height = Math.abs(
              parseInt(lineEl.getAttribute('x2'), 10) - parseInt(lineEl.getAttribute('x1'), 10)
            );
            const width = Math.abs(
              parseInt(lineEl.getAttribute('y2'), 10) - parseInt(lineEl.getAttribute('y1'), 10)
            );
            const x = parseInt(lineEl.getAttribute('x1'), 10);
            const y = parseInt(lineEl.getAttribute('y1'), 10);
            this.setState({
              isDrawing: true,
              rndBoxState: {
                x: x * this.state.scale,
                y: y * this.state.scale,
                width: width * this.state.scale,
                height: height * this.state.scale,
                drawRndBox: true,
                allowResize: false,
                drawLineEdges: true,
              },
            });
            this.bootstrapShape();
          }
          break;
      }
    }
  };

  handleEditEvent(element) {
    $('#' + element.getAttribute('id'))
      .off('click')
      .on('click', this.onShapeClick);
  }

  doRegisterClickOnShape = event => {
    switch (event.currentTarget.nodeName) {
      case 'rect':
        const bounds = event.currentTarget.getBoundingClientRect();
        const mousePos = {x: event.clientX, y: event.clientY};
        const innerThreshold = 15; // 15 px threshold
        return (
          Math.abs(mousePos.y - bounds.top) <= innerThreshold ||
          Math.abs(mousePos.x - bounds.left) <= innerThreshold ||
          Math.abs(mousePos.x - (bounds.left + bounds.width)) <= innerThreshold ||
          Math.abs(mousePos.y - (bounds.top + bounds.height)) <= innerThreshold
        );
      case 'ellipse':
        return this.isPointInsideEllipse(
          parseInt(parseInt(event.currentTarget.getAttribute('cx'), 10) * this.state.scale, 10),
          parseInt(parseInt(event.currentTarget.getAttribute('cy'), 10) * this.state.scale, 10),
          parseInt(parseInt(event.currentTarget.getAttribute('rx'), 10) * this.state.scale, 10),
          parseInt(parseInt(event.currentTarget.getAttribute('ry'), 10) * this.state.scale, 10),
          event.offsetX,
          event.offsetY
        );
      default:
        return true;
    }
  };

  isPointInsideEllipse = (cx, cy, rx, ry, px, py) => {
    const equation =
      Math.pow(px - cx, 2) / Math.pow(rx, 2) + Math.pow(py - cy, 2) / Math.pow(ry, 2);
    return equation >= 0.65;
  };

  onClickThroughShape = event => {
    this.props.onClickThroughShape(event);
  };

  handleShapeDrag() {
    const x = this.refs.box.draggable.state.x / this.state.scale;
    const y = this.refs.box.draggable.state.y / this.state.scale;
    const width = this.refs.box.resizable.state.width / this.state.scale;
    const height = this.refs.box.resizable.state.height / this.state.scale;
    let dragObj;
    if (this.refs.annotations.shapeDragElement.nodeName === DrawingCanvas.DRAWING_MODE_RECT) {
      dragObj = {
        x: x + 10 / this.state.scale,
        y: y + 10 / this.state.scale,
        width: width,
        height: height,
      };
    } else if (this.refs.annotations.shapeDragElement.nodeName !== 'line') {
      dragObj = {
        x: x,
        y: y,
        width: width,
        height: height,
      };
    } else {
      const el = this.refs.annotations.shapeDragElement;
      const x1 = parseInt(el.getAttribute('x1'), 10);
      const y1 = parseInt(el.getAttribute('y1'), 10);
      const x2 = parseInt(el.getAttribute('x2'), 10);
      const y2 = parseInt(el.getAttribute('y2'), 10);

      if (x1 < x2 && y1 < y2) {
        dragObj = {
          x1: x,
          y1: y,
          x2: x + width,
          y2: y + height,
        };
      } else if (x1 < x2 && y1 > y2) {
        dragObj = {
          x1: x,
          y1: y + height,
          x2: x + width,
          y2: y,
        };
      } else if (x1 > x2 && y1 > y2) {
        dragObj = {
          x1: x + width,
          y1: y + height,
          x2: x,
          y2: y,
        };
      } else if (x1 > x2 && y1 < y2) {
        dragObj = {
          x1: x + width,
          y1: y,
          x2: x,
          y2: y + height,
        };
      }
    }

    this.refs.annotations.handleShapeDrag(dragObj);
    this.alignRndBox();
  }

  alignRndBox() {
    const scale = this.state.scale;
    const el = this.refs.annotations.shapeDragElement;
    if (!el) return;
    let x, y, width, height;
    switch (el.nodeName) {
      case DrawingCanvas.DRAWING_MODE_RECT:
        {
          x = parseFloat(el.getAttribute('x')) * scale - 10;
          y = parseFloat(el.getAttribute('y')) * scale - 10;
          width = parseFloat(el.getAttribute('width')) * scale + 20;
          height = parseFloat(el.getAttribute('height')) * scale + 20;
        }
        break;
      case DrawingCanvas.DRAWING_MODE_ELLIPSE:
        {
          x = parseFloat(el.getAttribute('cx')) * scale;
          y = parseFloat(el.getAttribute('cy')) * scale;
          width = parseFloat(el.getAttribute('rx')) * scale;
          height = parseFloat(el.getAttribute('ry')) * scale;
          y -= height + 10;
          x -= width + 10;
          width += width + 20;
          height += height + 20;
        }
        break;
      case DrawingCanvas.DRAWING_MODE_LINE:
        {
          x = parseFloat(el.getAttribute('x1')) * scale;
          y = parseFloat(el.getAttribute('y1')) * scale;
          const x2 = parseFloat(el.getAttribute('x2')) * scale;
          const y2 = parseFloat(el.getAttribute('y2')) * scale;
          width = x2 > x ? x2 - x : x - x2;
          height = y2 > y ? y2 - y : y - y2;
          x = x2 < x ? x2 : x;
          y = y2 < y ? y2 : y;
        }
        break;
    }

    this.refs.box.updatePosition({x: x, y: y});
    this.refs.box.updateSize({width: width, height: height});
  }

  handleShapeResize() {
    const width = this.refs.box.resizable.state.width;
    const height = this.refs.box.resizable.state.height;
    const x = this.refs.box.draggable.state.x;
    const y = this.refs.box.draggable.state.y;
    let resizeObj;
    if (this.refs.annotations.shapeDragElement.nodeName === DrawingCanvas.DRAWING_MODE_RECT) {
      resizeObj = {
        width: (width - 20) / this.state.scale,
        height: (height - 20) / this.state.scale,
        x: (x + 10) / this.state.scale,
        y: (y + 10) / this.state.scale,
      };
    } else if (
      this.refs.annotations.shapeDragElement.nodeName === DrawingCanvas.DRAWING_MODE_ELLIPSE
    ) {
      resizeObj = {
        width: (width - 20) / this.state.scale,
        height: (height - 20) / this.state.scale,
        x: (x + 10) / this.state.scale,
        y: (y + 10) / this.state.scale,
      };
    } else {
      resizeObj = {
        width: width / this.state.scale,
        height: height / this.state.scale,
        x: x / this.state.scale,
        y: y / this.state.scale,
      };
    }

    this.refs.annotations.handleShapeResize(resizeObj);
    this.alignRndBox();
  }

  handleSVGClickOn() {
    $('body').on('click.shapeLostFocus', event => {
      if (event.target.id !== 'DCRndDivID' && !this.resizing) {
        this.resetActiveShape();
      }
    });
  }

  handleSVGClickOff() {
    $('body').off('click.shapeLostFocus');
  }

  drawRndBox() {
    let resize;
    if (!this.state.rndBoxState.allowResize) {
      resize = {
        top: false,
        right: false,
        bottom: false,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      };
    } else {
      resize = {
        top: true,
        right: true,
        bottom: true,
        left: true,
        topRight: true,
        bottomRight: true,
        bottomLeft: true,
        topLeft: true,
      };
    }
    return (
      <Rnd
        ref="box"
        default={{
          x: this.state.rndBoxState.x,
          y: this.state.rndBoxState.y,
          width: this.state.rndBoxState.width,
          height: this.state.rndBoxState.height,
        }}
        bounds="parent"
        onDrag={this.handleShapeDrag}
        onDragStop={() => {
          this.refs.annotations.handleShapeDragEnd();
          this.alignRndBox();
        }}
        onResizeStart={() => (this.resizing = true)}
        onResize={() => this.handleShapeResize()}
        onResizeStop={() => {
          this.refs.annotations.handleShapeDragEnd();
          this.alignRndBox();
          setTimeout(() => (this.resizing = false), 0);
        }}
        minWidth={40}
        minHeight={40}
        enableResizing={resize}>
        <div
          id="DCRndDivID"
          className="ShapeSelectInner"
          style={{
            borderImage: 'url(images/selection.gif) 1 round round',
          }}>
          {this.state.rndBoxState.drawLineEdges === false ? this.drawResizeEdges() : null}
        </div>
      </Rnd>
    );
  }

  drawResizeEdges() {
    return (
      <div>
        <span
          style={{
            width: '8px',
            height: '8px',
            position: 'absolute',
            top: '-4px',
            left: '-4px',
            background: '#FFF',
            border: '1px solid #000',
          }}
        />
        <span
          style={{
            width: '8px',
            height: '8px',
            position: 'absolute',
            top: '-4px',
            left: 'calc(50% - 4px)',
            background: '#FFF',
            border: '1px solid #000',
          }}
        />
        <span
          style={{
            width: '8px',
            height: '8px',
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            background: '#FFF',
            border: '1px solid #000',
          }}
        />
        <span
          style={{
            width: '8px',
            height: '8px',
            position: 'absolute',
            top: 'calc(50% - 4px)',
            right: '-4px',
            background: '#FFF',
            border: '1px solid #000',
          }}
        />
        <span
          style={{
            width: '8px',
            height: '8px',
            position: 'absolute',
            top: 'calc(100% - 5px)',
            right: '-4px',
            background: '#FFF',
            border: '1px solid #000',
          }}
        />
        <span
          style={{
            width: '8px',
            height: '8px',
            position: 'absolute',
            bottom: '-4px',
            right: 'calc(50% - 4px)',
            background: '#FFF',
            border: '1px solid #000',
          }}
        />
        <span
          style={{
            width: '8px',
            height: '8px',
            position: 'absolute',
            top: 'calc(100% - 5px)',
            left: '-4px',
            background: '#FFF',
            border: '1px solid #000',
          }}
        />
        <span
          style={{
            width: '8px',
            height: '8px',
            position: 'absolute',
            top: 'calc(50% - 4px)',
            left: '-4px',
            background: '#FFF',
            border: '1px solid #000',
          }}
        />
      </div>
    );
  }

  drawLineEditEdges() {
    const el = this.refs.annotations.shapeDragElement;
    if (!el) return;
    const x1 = parseInt(el.getAttribute('x1'), 10) * this.state.scale;
    const x2 = parseInt(el.getAttribute('x2'), 10) * this.state.scale;
    const y1 = parseInt(el.getAttribute('y1'), 10) * this.state.scale;
    const y2 = parseInt(el.getAttribute('y2'), 10) * this.state.scale;

    return (
      <div>
        <span
          id="DCResizeEdge1"
          style={{
            width: '8px',
            height: '8px',
            position: 'absolute',
            top: y1 - 4 + 'px',
            left: x1 - 4 + 'px',
            background: '#FFF',
            border: '1px solid #000',
            zIndex: '10',
          }}
        />
        <span
          id="DCResizeEdge2"
          style={{
            width: '8px',
            height: '8px',
            position: 'absolute',
            top: y2 - 4 + 'px',
            left: x2 - 4 + 'px',
            background: '#FFF',
            border: '1px solid #000',
            zIndex: '10',
          }}
        />
      </div>
    );
  }

  bootstrapShape() {
    const scope = this;
    const rndBoxEl = document.getElementById('DCRndDivID');
    $(rndBoxEl).on('mouseenter', () => {
      scope.handleSVGClickOn();
      this.cleanRndBoxLostFocusInterval();
    });

    const span1 = document.getElementById('DCResizeEdge1');
    const span2 = document.getElementById('DCResizeEdge2');
    $(span1)
      .add(span2)
      .on('mousedown', event => {
        if (this.refs.annotations.dragElement !== event.currentTarget) {
          scope.setState({
            rndBoxState: {
              drawRndBox: false,
              drawLineEdges: true,
            },
          });
          this.refs.annotations.dragElement = event.currentTarget;
        }
      });
  }

  cleanRndBoxLostFocusInterval() {
    if (this.rndBoxLostFocusInterval) {
      clearInterval(this.rndBoxLostFocusInterval);
      this.rndBoxLostFocusInterval = null;
    }
  }

  resetActiveShape = () => {
    this.handleSVGClickOff();
    this.props.shapeDrawingStop();
    this.setState({
      isDrawing: false,
      rndBoxState: {
        drawRndBox: false,
      },
    });
    this.cleanRndBoxLostFocusInterval();
    this.refs.annotations.handleShapeClickOff();
  };

  bootstrapTextArea() {
    const scope = this;

    setTimeout(() => {
      $('foreignObject textarea')
        .each(function() {
          this.setAttribute(
            'style',
            this.getAttribute('style') +
              'min-height:' +
              52 +
              'px;' +
              'height:' +
              (this.scrollHeight + 10) +
              'px;' +
              'width:' +
              (this.scrollWidth + 15) +
              'px;' +
              'overflow:hidden;'
          );
          scope.refs.annotations.handleDragStart(scope.refs.addTextComp.refs.dropButton);
        })
        .on('input', function() {
          scope.adjustTextAreaOnInput(this);
        });

      scope.handleTextAreaMouseEvents();

      $('foreignObject textarea').focus();
    }, 10);

    this.startLostInputFocusInterval();
  }

  handleTextAreaMouseEvents() {
    $('foreignObject textarea').on('mouseenter', () => {
      this.clearFontPickerDisappearInterval();
      $(this.refs.addTextComp.refs.parentPanel).css('display', 'block');
    });

    $('foreignObject textarea').on('mouseleave', () => {
      this.fontPickerDisappearTimeout = setInterval(() => {
        if (this.refs.addTextComp && $(this.refs.addTextComp.refs.parentPanel).is(':hover')) {
          return;
        }
        if (this.refs.addTextComp && !this.refs.addTextComp.isFocus()) {
          $(this.refs.addTextComp.refs.parentPanel).css('display', 'none');
          this.clearFontPickerDisappearInterval();
        }
      }, 1000);
    });
  }

  clearLostInputFocusInterval() {
    if (this.lostInputFocusInterval) {
      clearInterval(this.lostInputFocusInterval);
      this.lostInputFocusInterval = null;
    }
  }

  startLostInputFocusInterval() {
    let scope = this;
    this.lostInputFocusInterval = setInterval(() => {
      if (
        scope.refs.addTextComp &&
        !scope.refs.addTextComp.isFocus() &&
        !$('foreignObject textarea').is(':focus')
      ) {
        this.userFinishedInput();
      }
    }, 1000);
  }

  userFinishedInput() {
    clearInterval(this.lostInputFocusInterval);
    this.lostInputFocusInterval = null;
    let textEl = DCUtil.replaceTextAreaWithText(
      this.state.textElements,
      this.state.textModeState.textAreaNode
    );
    if (textEl !== null && textEl.getAttribute('ezdcid') === 'undefined') {
      let id = randomstring.generate() + '_text_' + ++this.lastId;
      textEl.setAttribute('ezdcid', id);
      textEl.setAttribute('id', id);
    } else if (textEl !== null) {
      textEl.setAttribute('id', textEl.getAttribute('ezdcid'));
    }
    this.setState({textElements: this.state.textElements});
    this.toggleTextInput();
    this.props.onTextInputHide();
  }

  cleanTextArea() {
    clearInterval(this.lostInputFocusInterval);
    this.lostInputFocusInterval = null;
    if (this.state.textModeState && this.state.textModeState.textAreaNode)
      this.refs.annotations.removeElem(this.state.textModeState.textAreaNode);
  }

  isSVGEmpty() {
    return this.refs.annotations.isAnnotationsEmpty();
  }

  calculateDefaultSize() {
    const {containerWidth} = this.state;
    if (containerWidth < 400) {
      return 12;
    }
    if (containerWidth < 1200) {
      return 24;
    }

    if (containerWidth < 2000) {
      return 36;
    }
  }

  userSelectedInputTextArea(event, style) {
    const defaultFontSize = this.calculateDefaultSize();
    const defaultTextProps =
      style !== undefined && !(style instanceof Event)
        ? style
        : {
            textValue: '',
            textColor: '#FFF',
            textSize: defaultFontSize,
            font: 'Arial',
          };
    const mouseX = Math.floor(event.nativeEvent.offsetX / this.state.scale);
    const mouseY = Math.floor(event.nativeEvent.offsetY / this.state.scale);
    const textAreaNode = DCUtil.appendTextArea(
      this.state.textElements,
      defaultTextProps.textValue,
      mouseX,
      mouseY,
      defaultTextProps
    );
    this.setState({textElements: this.state.textElements});
    window.render();
    this.bootstrapTextArea();
    this.setState({
      isDrawing: true,
      drawingMode: this.DRAWING_MODE_TEXT,
      textModeState: {
        userClicked: true,
        x: mouseX,
        y: mouseY,
        textAreaNode: textAreaNode,
      },
      textProps: defaultTextProps,
    });
  }

  textChangedByUser(value) {
    this.setState({
      textProps: {
        textValue: value,
      },
    });
  }

  textColorChanged(color) {
    this.setState({
      textProps: {
        ...this.state.textProps,
        textColor: color,
      },
    });
    $('foreignObject textarea').css('color', color);
  }

  textDeleted() {
    this.toggleTextInput();
  }

  autowideTextarea(textArea) {
    textArea.style.height = 'auto';
    textArea.style.height = textArea.scrollHeight + 10 + 'px';
    textArea.style.width = 'auto';
    textArea.style.width = textArea.scrollWidth + 10 + 'px';
  }

  adjustTextAreaOnInput(el, addNewLine = true) {
    this.autowideTextarea(el);
    let foreignObjectEl = $('#inputTextArea');
    const imageEl = $(this.refs.DrawingCanvasImage)[0];
    let overflow = calculateOverflow(imageEl, el);
    let textAreaOverflowX = parseInt(overflow.x, 10);
    const textAreaOverflowY = parseInt(overflow.y, 10);
    if (textAreaOverflowX > 0) {
      if (addNewLine && el.selectionStart === el.value.length) {
        el.value =
          el.value.slice(0, el.value.length - 1) + '\n' + el.value.slice(el.value.length - 1);
        this.autowideTextarea(el);
        overflow = calculateOverflow(imageEl, el);
        if (parseInt(overflow.x, 10) > 0) {
          textAreaOverflowX = parseInt(overflow.x, 10);
        } else {
          return;
        }
      }
      let newX = parseInt(foreignObjectEl.attr('x'), 10) - textAreaOverflowX / this.state.scale;
      const newY = parseInt(foreignObjectEl.attr('y'), 10);
      if (newX < 0) newX = 0;
      el.setAttribute('x', newX);
      foreignObjectEl.attr('x', newX);
      this.refs.addTextComp.onDrop({
        x: newX * this.state.scale,
        y: newY * this.state.scale - 20,
      });
      this.refs.addTextComp.setOpacity(1);
    }
    if (textAreaOverflowY > 0) {
      const newX = parseInt(foreignObjectEl.attr('x'), 10);
      let newY = parseInt(foreignObjectEl.attr('y'), 10) - textAreaOverflowY;
      if (newY < 0) newY = 0;
      el.setAttribute('y', newY);
      foreignObjectEl.attr('y', newY);
      this.refs.addTextComp.onDrop({
        x: newX * this.state.scale,
        y: newY * this.state.scale - 20,
      });
      this.refs.addTextComp.setOpacity(1);
    }
  }

  textFontChanged(font) {
    this.setState({
      textProps: {
        ...this.state.textProps,
        font: font.font,
        textSize: font.fontSize,
        bold: font.bold,
        italic: font.italic,
        underline: font.underline,
      },
    });
    let textAreaEl = $('foreignObject textarea')[0];
    $('foreignObject textarea').css('font-family', font.font);
    $('foreignObject textarea').css('font-size', font.fontSize + 'px');
    font.bold
      ? $('foreignObject textarea').css('font-weight', '700')
      : $('foreignObject textarea').css('font-weight', '400');
    font.italic
      ? $('foreignObject textarea').css('font-style', 'italic')
      : $('foreignObject textarea').css('font-style', 'normal');
    font.underline
      ? $('foreignObject textarea').css('text-decoration', 'underline')
      : $('foreignObject textarea').css('text-decoration', 'none');
    this.adjustTextAreaOnInput(textAreaEl, false);
  }

  maybeEnableScroll = () => {
    if (this.props.isContinuousPage && !this.state.canScroll) {
      this.setState({canScroll: true});
    }
  };

  maybeDisableScroll = () => {
    if (this.props.isContinuousPage && this.state.canScroll) {
      // Disable scrolling and make sure the entire image is visible.
      this.setState({canScroll: false});
      const offsetHeight = this.getPageHeight();
      const scrollTop = offsetHeight * (this.props.currentPage - 1);
      this.onScroll({target: {scrollTop}});
    }
  };

  getPageHeight = () => {
    return this.props.height * this.state.scale;
  };

  getInfScrollHeight = () => {
    return Math.min(this.getPageHeight() * this.props.numPages, MAX_HEIGHT);
  };

  getInfScrollStartPage = (props = this.props) => {
    const {currentPage, numPages} = props;
    let infScrollStartPage;
    if (currentPage < 10) {
      infScrollStartPage = 1;
    } else if (currentPage > numPages - 10) {
      infScrollStartPage = currentPage - 20;
    } else {
      infScrollStartPage = currentPage - 10;
    }

    return infScrollStartPage;
  };

  getOffsetY = () => {
    return this.props.isContinuousPage && this.state.canScroll ? 0 : this.state.offsetY;
  };

  onScroll = () => {
    if (this._ignoreNextScrollEvent) {
      this._ignoreNextScrollEvent = false;
      return;
    }

    clearTimeout(this._scrollTimer);
    this._scrollTimer = setTimeout(() => {
      const {scrollContainer} = this.refs;
      if (!scrollContainer) {
        return;
      }

      const pageHeight = this.getPageHeight();
      const scrollTop = (this._scrollTop = scrollContainer.scrollTop);
      const index =
        Math.round((scrollTop - this.state.scrollTopOffset) / pageHeight) +
        this.state.infScrollStartPage -
        1;
      this.checkInfiniteScroll();
      this.props.updatePage(index + 1);
    }, 50);
  };

  setPreventInfScrollTimer = (timeout = 500) => {
    this._preventInfScroll = true;
    clearTimeout(this._preventInfScrollTimer);
    this._preventInfScrollTimer = setTimeout(() => (this._preventInfScroll = false), timeout);
  };

  checkInfiniteScroll = () => {
    if (this._preventInfScroll) {
      return;
    }

    const {scrollContainer} = this.refs;
    const {infScrollStartPage} = this.state;
    const {numPages} = this.props;

    if (!scrollContainer) {
      return;
    }

    const pageHeight = this.getPageHeight();
    const nearTop = scrollContainer.scrollTop < 100;
    const nearBottom =
      scrollContainer.scrollTop > scrollContainer.scrollHeight - scrollContainer.clientHeight - 100;

    let nextInfScrollStartPage;

    if (nearTop && infScrollStartPage > 1) {
      // Prepend up to 20 pages of scroll space.
      nextInfScrollStartPage = Math.max(infScrollStartPage - 20, 1);
      const count = infScrollStartPage - nextInfScrollStartPage;
      this._scrollTop = scrollContainer.scrollTop + pageHeight * count;
    } else if (
      nearBottom &&
      infScrollStartPage <
        numPages - (this.getInfScrollHeight() - this.state.scrollTopOffset) / pageHeight
    ) {
      nextInfScrollStartPage = Math.min(infScrollStartPage + 20, numPages);
      this._scrollTop = scrollContainer.scrollTop - pageHeight * 20;
    }

    if (nextInfScrollStartPage) {
      this._ignoreNextScrollEvent = true;
      scrollContainer.scrollTop = this._scrollTop;
      this.setState({infScrollStartPage: nextInfScrollStartPage});
    }
  };

  recalculateScrollTop = () => {
    this._recalculateScrollTop = true;
  };

  renderInfiniteScrollContainer(children) {
    if (!(this.props.isContinuousPage && this.state.canScroll)) {
      return children;
    }

    const style = {
      height: this.getInfScrollHeight(),
    };

    return (
      <div
        ref="scrollContainer"
        className="DrawingCanvasInfiniteScrollContainer"
        onScroll={this.onScroll}>
        <div style={style}>{children}</div>
      </div>
    );
  }

  render() {
    this.onDOMDrawn();
    let containerStyle = {
      position: 'absolute',
      left: this.state.offsetX,
      width: this.props.width * this.state.scale,
      height: this.props.height * this.state.scale,
    };

    let style = {};
    if (this.props.isContinuousPage && this.state.canScroll) {
      const pageHeight = this.getPageHeight();
      style.top =
        (this.props.currentPage - 1) * pageHeight -
        (this.state.infScrollStartPage - 1) * pageHeight +
        this.state.scrollTopOffset;
    } else {
      containerStyle.top = this.state.offsetY;
    }

    return this.renderInfiniteScrollContainer(
      <div ref="container" className="DrawingCanvasContainer" style={style}>
        {this.state.cropMode && !this.props.isUnavailable ? (
          <CropImageComponent
            ref="crop"
            width={this.props.width}
            height={this.props.height}
            scale={this.state.scale}
            offsetX={this.state.offsetX}
            offsetY={this.state.offsetY}
            onCropResizeStop={this.props.onCropResizeStop}
          />
        ) : null}
        <div ref="DrawingCanvasImage" style={containerStyle}>
          {this.state.rndBoxState.drawRndBox && !this.props.isUnavailable
            ? this.drawRndBox()
            : null}
          {this.state.rndBoxState.drawLineEdges && !this.props.isUnavailable
            ? this.drawLineEditEdges()
            : null}
        </div>
        {!this.props.isUnavailable ? (
          <canvas
            ref="canvas"
            width={this.state.containerWidth}
            height={this.state.containerHeight}
          />
        ) : null}
        {this.drawAnnotations()}
        {this.drawInput()}
      </div>
    );
  }
}

const styles = {
  textInputWrap: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  textInputClickArea: {
    width: '100%',
    height: '100%',
  },
};
